import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { companyName, UserActivityType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import {
  downloadAllUserActivitiesCSV,
  downloadCSVfilterUserActivities,
  downloadPDFfilterUserActivities,
  filterAllUserActivities,
  filterUserActivities,
  filterUserActivity,
} from 'services'
import { svgLoading } from 'stories/assets'
import {
  Button,
  Input2,
  MultiSelect,
  Pagination,
  Tooltip,
} from 'stories/components'
import { filterObject, openUserActivityDlg } from 'utils'
import { formatDate, formatTime } from 'utils/convertor'
import { useTitle } from 'utils/pageTitle'

import { DownloadAllActivityModal } from './DownloadAllModal'
import { ALL_USERS_ID, IActiveUser, IUserActivity } from './type'

const itemCountPerPage = 10

const allUsersOption = (totalCount: number): IActiveUser => ({
  id: ALL_USERS_ID,
  email: 'All Users',
  accountType: '' as IActiveUser['accountType'],
  count: `${totalCount}`,
})

const buildAccountTypeParam = (accountType: Record<string, boolean>) => {
  const selected = filterObject(accountType)
  return selected.length ? selected.join(',') : ''
}

export default function UserActivity() {
  useTitle(`User Activity - ${companyName}`)

  const [filters, setFilters] = useState<Record<string, any>>({
    startDate: '',
    endDate: '',
    email: '',
    accountType: {},
  })

  const [users, setUsers] = useState<Array<IActiveUser>>([])
  const [activeUser, setActiveUser] = useState<IActiveUser | null>(null)
  const [activities, setActivities] = useState<Array<IUserActivity>>([])
  const [total, setTotal] = useState(0)
  const [action, setAction] = useState('')
  const [pageNum, setPageNum] = useState(0)
  const [lastDays, setLastDays] = useState(0)
  const [isDownloadAllModal, setDownloadAllModal] = useState(false)

  useEffect(() => {
    onLastDays(7)
  }, [])

  const buildFilterQuery = (filters: Record<string, any>) => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    email: filters.email,
    accountType: buildAccountTypeParam(filters.accountType),
  })

  const filterUsersData = async (filters: Record<string, any>) => {
    setAction('getUsers')
    let data = (await filterUserActivity(buildFilterQuery(filters))) as IActiveUser[]
    data = data.filter((item) => !!Number(item.count))
    setUsers(data)
    setAction('')
    setActivities([])
    if (data.length) onSelectAllUsers(filters, data)
    else setActiveUser(null)
  }

  const onChangeFilter = (key: 'startDate' | 'endDate' | 'email' | 'accountType', value: any) => {
    const newFilters = Object.assign({}, filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key !== 'email' && key !== 'accountType') {
      setLastDays(0)
      filterUsersData(newFilters)
    }
  }

  const onLastDays = (days: number) => {
    const newFilters = Object.assign({}, filters)
    let today = DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd')
    newFilters.startDate = DateTime.now().minus({ days }).toFormat('yyyy-MM-dd')
    newFilters.endDate = today

    filterUsersData(newFilters)
    setFilters(newFilters)
    setLastDays(days)
  }

  const onGetResult = () => {
    filterUsersData(filters)
  }

  const isAllUsersView = activeUser?.id === ALL_USERS_ID

  const fetchActivities = async (filters: Record<string, any>, skip: number) => {
    if (activeUser?.id === ALL_USERS_ID) {
      return filterAllUserActivities({
        ...buildFilterQuery(filters),
        count: itemCountPerPage,
        skip,
      })
    }
    return filterUserActivities(activeUser!.id, {
      startDate: filters.startDate,
      endDate: filters.endDate,
      count: itemCountPerPage,
      skip,
    })
  }

  const onSelectAllUsers = async (filters: Record<string, any>, userList = users) => {
    const totalCount = userList.reduce((sum, user) => sum + Number(user.count), 0)
    setAction('getUsersActivities')
    setActiveUser(allUsersOption(totalCount))
    const { data, total } = await filterAllUserActivities({
      ...buildFilterQuery(filters),
      count: itemCountPerPage,
      skip: 0,
    })
    setActivities(data)
    setTotal(total)
    setPageNum(0)
    setAction('')
  }

  const onSelectUser = async (user: IActiveUser, filters: Record<string, any>) => {
    setAction('getUsersActivities')
    setActiveUser(user)
    const { data, total } = await filterUserActivities(user.id, {
      startDate: filters.startDate,
      endDate: filters.endDate,
      count: itemCountPerPage,
      skip: 0,
    })
    setActivities(data)
    setTotal(total)
    setPageNum(0)
    setAction('')
  }

  const onSelectActivity = async (activity: IUserActivity) => {
    if (
      [UserActivityType.LOGIN, UserActivityType.FORGET_PASSWORD, UserActivityType.RESET_PASSWORD].indexOf(
        activity.type,
      ) !== -1
    )
      return
    openUserActivityDlg(activity)
  }

  const onPageNavigate = async (num: number) => {
    if (!activeUser) return
    setPageNum(num)

    setAction('getUsersActivities')
    const { data, total } = await fetchActivities(filters, num * itemCountPerPage)
    setActivities(data)
    setTotal(total)
    setAction('')
  }

  const onDownloadCSV = async () => {
    if (isAllUsersView) {
      setDownloadAllModal(true)
      return
    }
    await downloadCSVfilterUserActivities(activeUser?.id, {
      startDate: filters.startDate,
      endDate: filters.endDate,
      email: activeUser?.email,
      count: total,
      skip: 0,
    })
  }

  const onDownloadPDF = async () => {
    await downloadPDFfilterUserActivities(activeUser?.id, {
      startDate: filters.startDate,
      endDate: filters.endDate,
      email: activeUser?.email,
      count: total,
      skip: 0,
    })
  }

  const onDownloadAll = async (excludeUserIds: string[], excludeAccountTypes: string[]) => {
    setDownloadAllModal(false)
    const query = buildFilterQuery(filters)
    await downloadAllUserActivitiesCSV({
      ...query,
      excludeUserIds: excludeUserIds.join(','),
      excludeAccountTypes: excludeAccountTypes.join(','),
    })
  }

  return (
    <div className="registration-container py-6 px-2">
      <div className="shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-5 sm:text-center lg:text-left w-full">
        <div className="flex flex-wrap justify-between mb-1">
          <h1 className="text-xl font-bold flex items-center">
            <span>User Activity Filter Options</span>{' '}
          </h1>
        </div>
        <div className="block rounded h-px bg-gray-300 mb-6"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 items-center">
          <Input2
            type="date"
            title="Start Date"
            value={filters.startDate}
            onChange={(value) => onChangeFilter('startDate', value)}
          />
          <Input2
            type="date"
            title="End Date"
            value={filters.endDate}
            onChange={(value) => onChangeFilter('endDate', value)}
          />
          <Button size="sm" full={false} outline={lastDays !== 7} onClick={() => onLastDays(7)}>
            Last 7 days
          </Button>
          <Button size="sm" full={false} outline={lastDays !== 30} onClick={() => onLastDays(30)}>
            Last 30 days
          </Button>
          <Input2
            type="email"
            title="Search by email"
            value={filters.email}
            onChange={(value) => onChangeFilter('email', value)}
          />
          <MultiSelect
            id="search-profile"
            title="Search by profile"
            options={accountTypes}
            defaultOptionText="All"
            hasDefaultOption
            value={filters.accountType}
            defaultSelected={false}
            onChange={(value) => onChangeFilter('accountType', value)}
            onBlur={onGetResult}
          />
          <Button size="sm" onClick={onGetResult}>
            Get Result
          </Button>
        </div>
      </div>

      <div className="max-w-screen-2xl m-auto flex justify-between lg:flex-row flex-col mt-4">
        <div className="shadow1 mb-4 lg:w-[30%] w-full bg-white rounded p-3 md:p-4 sm:text-center lg:text-left mr-3">
          <div className="flex flex-wrap justify-between mb-1">
            <h1 className="text-xl font-bold flex items-center">
              <span>Users</span>
            </h1>
          </div>
          <div className="block rounded h-px bg-gray-300 mb-3"></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-2 py-3">
                    No
                  </th>
                  <th scope="col" className="px-2 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-2 py-3">
                    Type
                  </th>
                  <th scope="col" className="px-2 py-3">
                    Activities
                  </th>
                </tr>
              </thead>
              <tbody className="">
                <tr
                  className={`hover:bg-gray-100 border-b dark:bg-gray-800 dark:border-gray-700 cursor-pointer font-semibold ${
                    isAllUsersView ? 'bg-gray-200' : 'bg-white'
                  }`}
                  onClick={() => onSelectAllUsers(filters)}
                >
                  <th scope="row" className="px-2 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                    —
                  </th>
                  <td className="px-2 py-3 text-shade-blue">All Users</td>
                  <td className="px-2 py-3">—</td>
                  <td className="px-2 py-3 text-center">{users.reduce((sum, user) => sum + Number(user.count), 0)}</td>
                </tr>
                {users.map((user, index) => {
                  const isActive = activeUser && activeUser.id == user.id && !isAllUsersView
                  return (
                    <tr
                      className={`hover:bg-gray-100 border-b dark:bg-gray-800 dark:border-gray-700 cursor-pointer ${
                        isActive ? 'bg-gray-200' : 'bg-white'
                      }`}
                      key={`${index}`}
                      onClick={() => onSelectUser(user, filters)}
                    >
                      <th scope="row" className="px-2 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {index + 1}
                      </th>
                      <td className="px-2 py-3">{user.email}</td>
                      <td className="px-2 py-3">{(accountTypes as any)[user.accountType]}</td>
                      <td className="px-2 py-3 text-center">{user.count}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="shadow1 mb-4 max-w-screen-2xl bg-white rounded p-3 md:p-4 sm:text-center lg:text-left flex-auto">
          <div className="relative overflow-visible sm:rounded-lg">
            <div className="flex justify-between">
              <div className="flex flex-wrap mb-1">
                <h1 className="text-xl font-bold flex items-center mr-3">
                  <span>Activity of</span>
                </h1>
                {activeUser && (
                  <h1 className="text-xl font-bold flex items-center text-shade-blue mr-3">
                    <span>{isAllUsersView ? 'All Users' : activeUser.email}</span>
                  </h1>
                )}
                <h1 className="text-md flex items-center mt-1">
                  <span>
                    ({formatDate(filters.startDate)} - {formatDate(filters.endDate)})
                  </span>
                </h1>
              </div>
              <div className="flex">
                <span className="mt-1 p-2 text-sm cursor-pointer text-shade-blue" onClick={onDownloadCSV}>
                  <Tooltip message={isAllUsersView ? 'Download All Users (CSV)' : 'Download CSV'}>
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
                {!isAllUsersView && (
                  <span className="mt-1 p-2 text-sm cursor-pointer text-shade-blue" onClick={onDownloadPDF}>
                    <Tooltip message="Download PDF">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </Tooltip>
                  </span>
                )}
              </div>
            </div>
            {action === 'getUsersActivities' ? (
              <div className="absolute left-0 top-0 right-0 bottom-0 bg-white/50 dark:bg-slate-900/50 place-items-center flex">
                <div className="flex flex-col w-full place-items-center">
                  <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
                </div>
              </div>
            ) : null}

            <div className="block rounded h-px bg-gray-300 mb-3"></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
                <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="pl-6 py-3">
                      No
                    </th>
                    {isAllUsersView && (
                      <th scope="col" className="py-3">
                        Email
                      </th>
                    )}
                    <th scope="col" className="py-3">
                      Loan Number
                    </th>
                    <th scope="col" className="">
                      Action
                    </th>
                    <th scope="col" className="">
                      Date
                    </th>
                    <th scope="col" className=""></th>
                  </tr>
                </thead>
                <tbody className="">
                  {activities.map((activity, index) => {
                    return (
                      <tr
                        className="bg-white hover:bg-gray-100 border-b dark:bg-gray-800 dark:border-gray-700"
                        key={`${pageNum * itemCountPerPage + index}`}
                      >
                        <th
                          scope="row"
                          className="pl-6 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap"
                        >
                          {pageNum * itemCountPerPage + index + 1}
                        </th>
                        {isAllUsersView && <td>{activity.userEmail || ''}</td>}
                        <td>{activity.loanNumber === 0 ? '' : activity.loanNumber}</td>
                        <td>{activity.action}</td>
                        <td>{formatTime(activity.createdAt)}</td>
                        <td>
                          {Object.keys(activity.detail).length > 0 ? (
                            <span className="cursor-pointer hover:underline" onClick={() => onSelectActivity(activity)}>
                              Details
                            </span>
                          ) : (
                            <span></span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end items-center mt-3 mb-3">
              <Pagination
                totalCount={total}
                itemCountPerPage={itemCountPerPage}
                onNavigate={onPageNavigate}
                pageNum={pageNum}
              />
            </div>
          </div>
        </div>
      </div>

      {isDownloadAllModal && (
        <DownloadAllActivityModal users={users} onClose={() => setDownloadAllModal(false)} onDownload={onDownloadAll} />
      )}
    </div>
  )
}
